import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import AnimatedModal from "components/common/modals/AnimatedModal";
import "./BookingSuccessModal.css";

interface BookingSuccessModalProps {
  isOpen: boolean;
  onComplete: () => void;
  /** Duration in ms before auto-navigating. Default: 2000 */
  duration?: number;
}

const BookingSuccessModal: React.FC<BookingSuccessModalProps> = ({
  isOpen,
  onComplete,
  duration = 2000,
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      timerRef.current = setTimeout(() => {
        onComplete();
      }, duration);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isOpen, duration, onComplete]);

  return (
    <AnimatedModal isOpen={isOpen} onClose={onComplete} maxWidth={320}>
      <div className="booking-success-modal__inner">
        {/* Animated check ring */}
        <motion.div
          className="booking-success-modal__ring"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            delay: 0.08,
            type: "spring",
            stiffness: 220,
            damping: 16,
          }}
        >
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: 0.22,
              type: "spring",
              stiffness: 260,
              damping: 18,
            }}
          >
            <CheckCircle className="booking-success-modal__icon" />
          </motion.div>
        </motion.div>

        {/* Text */}
        <motion.p
          className="booking-success-modal__title"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.25 }}
        >
          Booked!
        </motion.p>
        <motion.p
          className="booking-success-modal__sub"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.38, duration: 0.22 }}
        >
          Your appointment has been booked!
        </motion.p>

        {/* Progress bar */}
        <motion.div
          className="booking-success-modal__progress"
          initial={{ scaleX: 1 }}
          animate={{ scaleX: 0 }}
          transition={{
            duration: duration / 1000,
            ease: "linear",
            delay: 0.05,
          }}
          style={{ transformOrigin: "left center" }}
        />
      </div>
    </AnimatedModal>
  );
};

export default BookingSuccessModal;
